import React, { useState, useEffect, useRef } from 'react'
import { ProgressBar } from 'primereact/progressbar'
import { Card } from 'primereact/card'
import { Message } from 'primereact/message'
import useAxios from '../../hooks/useAxios'
import { useAuth } from '../../contexts/AuthContext'
import MsisdnSearch from '../../components/MsisdnSearch'

const useSimulatedProgress = (active, slow) => {
  const [progress, setProgress] = useState(0)
  const intervalRef = useRef(null)

  useEffect(() => {
    if (active) {
      clearInterval(intervalRef.current)
      setProgress(0)
      intervalRef.current = setInterval(() => {
        setProgress(prev => {
          if (slow) {
            if (prev >= 70) return prev + 0.1
            if (prev >= 50) return prev + 0.2
            return prev + 0.4
          }
          if (prev >= 50) return prev + 0.2
          if (prev >= 40) return prev + 0.3
          return prev + 3
        })
      }, 300)
      return () => {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    } else {
      setProgress(prev => (prev > 0 ? 100 : 0))
      const t = setTimeout(() => setProgress(0), 400)
      return () => clearTimeout(t)
    }
  }, [active, slow])

  return Math.min(Math.round(progress), 100)
}

const UsrinfV2 = () => {
  const { user } = useAuth()

  const { fetchData: fetchCS, response: csResponse, error: csError } = useAxios()
  const { fetchData: fetchLTE, response: lteResponse, error: lteError } = useAxios()

  const [csData, setCsData] = useState(null)
  const [lteData, setLteData] = useState(null)
  const [phase, setPhase] = useState('')
  const [searching, setSearching] = useState(false)
  const msisdnRef = useRef(null)

  const progress = useSimulatedProgress(searching, phase.includes('LTE') || phase.includes('2G/3G. Поиск'))

  // 1. Start search — query all MSCs in parallel
  const handleSearch = (msisdn) => {
    setCsData(null)
    setLteData(null)
    setPhase('Поиск в сети 2G/3G...')
    setSearching(true)
    msisdnRef.current = msisdn
    fetchCS({ method: 'get', url: '/api/usrinf/v2/cs', params: { value: msisdn } })
  }

  // 2. CS response arrived
  useEffect(() => {
    if (!csResponse) return
    setCsData(csResponse)

    const found = csResponse.result === 'success'
    const associated = found && csResponse.data['SGs Association Status'] === 'Associated'

    if (associated) {
      // found in 2G/3G + associated → query specific MME
      setPhase('Поиск в сети LTE...')
      fetchLTE({ method: 'get', url: '/api/usrinf/v2/lte', params: { value: msisdnRef.current, mmeNumber: csResponse.data['MME Number'], mscHost: csResponse.mscHost, csCellName: csResponse.csCellName } })
    } else if (!found) {
      // not found in any MSC → query all MMEs
      setPhase('Абонент не зарегистрирован в сети 2G/3G. Поиск в сети LTE...')
      fetchLTE({ method: 'get', url: '/api/usrinf/v2/lte', params: { value: msisdnRef.current } })
    } else {
      // found in 2G/3G, not associated → done
      setSearching(false)
    }
  }, [csResponse])

  // 3. LTE response arrived
  useEffect(() => {
    if (!lteResponse) return
    setLteData(lteResponse)
    setSearching(false)
  }, [lteResponse])

  // 4. Handle errors — stop searching
  useEffect(() => { if (csError) setSearching(false) }, [csError])
  useEffect(() => { if (lteError) setSearching(false) }, [lteError])

  if (!user) return null

  // derive display flags from data
  const csFound = csData?.result === 'success'
  const csNotFound = csData?.result === 'fail'
  const notAssociated = csFound && csData.data['SGs Association Status'] !== 'Associated'
  const lteFound = lteData?.result === 'success'
  const lteNotFound = lteData?.result === 'fail'
  const voWiFi = csData?.voWiFi
  const voWiFiFound = voWiFi?.found

  return (
    <>
      <MsisdnSearch onSubmit={handleSearch} loading={searching} title="Местоположение абонента" />

      {/* Progress bar */}
      {searching && (
        <div className="px-3 mt-5">
          <div className="text-white font-bold mb-2 text-center">{phase}</div>
          <ProgressBar value={progress} style={{ height: '24px' }} showValue={true} />
        </div>
      )}

      {/* 2G/3G data */}
      {csFound && (
        <div className="px-3 mt-5">
          <Card title="Местоположение абонента в сети 2G/3G">
            {Object.entries(csData.data).map(([key, val]) => (
              <div key={key}><b>{key}:</b> {val}</div>
            ))}
          </Card>
        </div>
      )}

      {/* Found in 2G/3G but not in LTE */}
      {notAssociated && (
        <div className="px-3 mt-3">
          <Message severity="info" text="Абонент не зарегистрирован в LTE-сети" className="w-full" />
        </div>
      )}

      {/* LTE data */}
      {lteFound && (
        <div className="px-3 mt-3">
          <Card title='Местоположение абонента в сети LTE'>
            <div><b>ECI:</b> {lteData.data.eci}</div>
            {lteData.data.cellName && <div><b>Cell Name:</b> {lteData.data.cellName}</div>}
            {lteData.data.cellNotInDb && <div className="text-yellow-400 mt-2">ECI не занесен в БД (новая сота)</div>}
          </Card>
        </div>
      )}

      {/* VoWiFi (ePDG CDR) data */}
      {voWiFiFound && (
        <div className="px-3 mt-3">
          <Card title="Данные абонента в VoWiFi">
            <div><b>Начало сессии:</b> {voWiFi.startTime}</div>
            <div><b>Конец сессии:</b> {voWiFi.stopTime}</div>
            <div><b>Длительность:</b> {voWiFi.durationHms}</div>
            <div><b>IP-адрес:</b> {voWiFi.ueTunnelIp}</div>
          </Card>
        </div>
      )}

      {/* Not found in 2G/3G and not found in LTE either */}
      {!searching && csNotFound && lteNotFound && !voWiFiFound && (
        <div className="px-3 mt-3">
          <Message severity="error" text="Абонент не зарегистрирован ни в сети 2G/3G, ни в сети LTE" className="w-full" />
        </div>
      )}

      {/* Found in 2G/3G, associated, but LTE query returned nothing */}
      {!searching && csFound && !notAssociated && lteNotFound && (
        <div className="px-3 mt-3">
          <Message severity="info" text={lteData.msg} className="w-full" />
        </div>
      )}

      {/* Network or server error */}
      {!searching && (csError || lteError) && (
        <div className="px-3 mt-5">
          <Message severity="error" text="Ошибка при запросе. Попробуйте снова." className="w-full" />
        </div>
      )}
    </>
  )
}

export default UsrinfV2
